import type { AssetId, AssetValue, Market, OrderbookDepth, Ticker } from "@/lib/wsTypes";

/**
 * Convert a u256 BigInt (stored via from_be_bytes on the Rust side) back to
 * the original hash as a hex string.
 *
 * SpacetimeDB stores ScriptHash/Ed25519KeyHash as u256 using
 * `u256::from_be_bytes([0;4] ++ hash_bytes)`, so the raw hash bytes are the
 * big-endian (least-significant) bytes of the BigInt, i.e. the last `byteLen`
 * bytes when the value is written out MSB-first.
 */
export function u256ToHashHex(value: bigint, byteLen = 28): string {
  return value.toString(16).padStart(64, "0").slice(-byteLen * 2);
}

/**
 * Convert a raw hash hex string to the u256 BigInt that SpacetimeDB stores.
 * Inverse of `u256ToHashHex`.
 */
export function hashHexToU256(hex: string): bigint {
  return BigInt("0x" + hex);
}

// CIP-68 asset name prefixes (4 bytes each)
const CIP68_PREFIXES = [
  "000de140", // (222) Reference NFT
  "000643b0", // (100) NFT
  "0014df10", // (333) FT
] as const;

/** Strip CIP-68 4-byte prefix from raw asset name bytes, if present. */
function stripCip68Prefix(bytes: Uint8Array): Uint8Array {
  if (bytes.length <= 4) return bytes;
  const hex = Buffer.from(bytes.slice(0, 4)).toString("hex");
  if (CIP68_PREFIXES.includes(hex as (typeof CIP68_PREFIXES)[number])) {
    return bytes.slice(4);
  }
  return bytes;
}

/** Human-readable label for an AssetId. */
export function formatAssetLabel(asset: AssetId): string {
  if (asset.tag === "Ada") return "ADA";
  const rawBytes = Buffer.from((asset as any).assetName ?? "", "hex");
  const bytes = stripCip68Prefix(rawBytes);
  try {
    const name = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    if (/^[\x20-\x7E]+$/.test(name)) return name;
  } catch {
    // not valid utf-8
  }
  return Buffer.from(bytes).toString("hex").slice(0, 8).toUpperCase();
}

/** "BASE/QUOTE" label for a market. */
export function formatMarketPairLabel(market: Market): string {
  return `${formatAssetLabel(market.tradingPair.base)}-${formatAssetLabel(market.tradingPair.quote)}`;
}

/** Stable string key for an AssetId (for equality checks / Map keys). */
export function assetIdKey(asset: AssetId): string {
  if (asset.tag === "Ada") return "ada";
  return `${(asset as any).policyId}.${(asset as any).assetName}`;
}


/** Token descriptor used by the swap UI. */
export interface SwapToken {
  key: string;
  label: string;
  asset: AssetId;
}

/** Collect all unique tokens that appear in any market (both sides). */
export function collectTokens(markets: readonly Market[]): SwapToken[] {
  const seen = new Map<string, SwapToken>();
  for (const m of markets) {
    for (const asset of [m.tradingPair.base, m.tradingPair.quote]) {
      const key = assetIdKey(asset);
      if (!seen.has(key)) {
        seen.set(key, { key, label: formatAssetLabel(asset), asset });
      }
    }
  }
  return [...seen.values()];
}

/**
 * Find a market that matches a given sell/buy token pair.
 * Checks both orientations (base→quote and quote→base).
 */
export function findMarketForPair(
  sellKey: string,
  buyKey: string,
  markets: readonly Market[],
): { market: Market; inverted: boolean } | null {
  for (const m of markets) {
    const baseKey = assetIdKey(m.tradingPair.base);
    const quoteKey = assetIdKey(m.tradingPair.quote);
    if (baseKey === sellKey && quoteKey === buyKey) return { market: m, inverted: false };
    if (quoteKey === sellKey && baseKey === buyKey) return { market: m, inverted: true };
  }
  return null;
}

import { getTokenDecimals } from "./tokenMetadata";

/** Get the number of decimal places for an asset. */
export function getAssetDecimals(asset: AssetId): number {
  return getTokenDecimals(formatAssetLabel(asset));
}

/**
 * Format a raw asset value for display, applying the correct decimal places.
 * Decimals are read from public/tokens/*.json metadata files (default: 6).
 */
export function formatAssetValue(asset: AssetId, rawValue: bigint): string {
  const decimals = getAssetDecimals(asset);
  if (decimals === 0) return rawValue.toLocaleString();
  const divisor = 10n ** BigInt(decimals);
  const whole = rawValue / divisor;
  const frac = rawValue % divisor;
  const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
  return fracStr
    ? `${whole.toLocaleString()}.${fracStr}`
    : whole.toLocaleString();
}

/** Look up the raw balance for a given asset key in an assets array. */
export function findAssetBalance(
  assets: readonly AssetValue[],
  tokenKey: string,
): bigint {
  for (const av of assets) {
    if (assetIdKey(av.asset) === tokenKey) return av.value;
  }
  return 0n;
}

export interface AggregatedAssetBalance {
  asset: AssetId;
  key: string;
  value: bigint;
}

export function aggregateAssetBalances(
  assets: readonly AssetValue[],
): AggregatedAssetBalance[] {
  const balances = new Map<string, AggregatedAssetBalance>();

  for (const assetValue of assets) {
    const key = assetIdKey(assetValue.asset);
    const current = balances.get(key);

    if (current) {
      current.value += assetValue.value;
      continue;
    }

    balances.set(key, {
      asset: assetValue.asset,
      key,
      value: assetValue.value,
    });
  }

  return [...balances.values()];
}

/**
 * Convert a market AssetId to the wallet's asset ID format ("policyIdHex.assetNameHex").
 * ADA maps to "ada.lovelace".
 */
export function marketAssetToWalletId(asset: AssetId): string {
  if (asset.tag === "Ada") return "ada.lovelace";
  return `${(asset as any).policyId}.${(asset as any).assetName}`;
}

/**
 * Collect all unique wallet-format asset IDs that appear in any market.
 */
export function collectMarketWalletIds(markets: readonly Market[]): Set<string> {
  const ids = new Set<string>();
  for (const m of markets) {
    ids.add(marketAssetToWalletId(m.tradingPair.base));
    ids.add(marketAssetToWalletId(m.tradingPair.quote));
  }
  return ids;
}

/** Convert an AssetId to the Hydrozoa API JSON format. */
export function assetIdToApi(asset: AssetId): { tag: "Ada" } | { tag: "NativeToken"; policyId: string; assetName: string } {
  if (asset.tag === "Ada") return { tag: "Ada" };
  return { tag: "NativeToken", policyId: (asset as any).policyId, assetName: (asset as any).assetName };
}

const PRICE_FRAC_BITS = 60n;

/** Convert a fixed-point u128 price (60 fractional bits) to a display number. */
export function priceKeyToNumber(raw: bigint): number {
  const integer = Number(raw >> PRICE_FRAC_BITS);
  const fractional = Number(raw & ((1n << PRICE_FRAC_BITS) - 1n)) / Number(1n << PRICE_FRAC_BITS);
  return integer + fractional;
}

/**
 * Current mid price of a market (quote per base). Prefers the live orderbook
 * depth ((bestBid + bestAsk) / 2, or whichever side exists); when this client
 * has no book for the market (only the viewed market's orderbook is
 * subscribed), falls back to the market's ticker -- available for ALL markets
 * -- so unvisited markets still price instead of valuing holdings at zero.
 * Returns null only when neither source has a price.
 */
export function marketMidPrice(
  market: Market,
  depths: Record<string, readonly OrderbookDepth[]>,
  tickers?: Record<string, Ticker>,
): number | null {
  const levels = depths[market.key];
  if (levels && levels.length > 0) {
    let bestBid = -Infinity;
    let bestAsk = Infinity;
    for (const level of levels) {
      const price = priceKeyToNumber(level.price);
      if (level.bidAmount > 0n && price > bestBid) bestBid = price;
      if (level.askAmount > 0n && price < bestAsk) bestAsk = price;
    }
    const hasBid = bestBid > -Infinity;
    const hasAsk = bestAsk < Infinity;
    if (hasBid && hasAsk) return (bestBid + bestAsk) / 2;
    if (hasBid) return bestBid;
    if (hasAsk) return bestAsk;
  }
  const ticker = tickers?.[market.key];
  if (ticker) {
    if (ticker.bestBid != null && ticker.bestAsk != null) {
      return (priceKeyToNumber(ticker.bestBid) + priceKeyToNumber(ticker.bestAsk)) / 2;
    }
    if (ticker.bestBid != null) return priceKeyToNumber(ticker.bestBid);
    if (ticker.bestAsk != null) return priceKeyToNumber(ticker.bestAsk);
    if (ticker.lastPrice != null) return priceKeyToNumber(ticker.lastPrice);
  }
  return null;
}

/** Get the set of token keys that can pair with a given token across all markets. */
export function pairableTokenKeys(
  tokenKey: string,
  markets: readonly Market[],
): Set<string> {
  const result = new Set<string>();
  for (const m of markets) {
    const baseKey = assetIdKey(m.tradingPair.base);
    const quoteKey = assetIdKey(m.tradingPair.quote);
    if (baseKey === tokenKey) result.add(quoteKey);
    if (quoteKey === tokenKey) result.add(baseKey);
  }
  return result;
}
